import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import categoryService from '../../services/categoryService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import CreateCategoryDialog from './CreateCategoryDialog';

const CategoriesIndex = () => {
  const { get, isRtl } = useLocalization();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذا التصنيف؟' : 'Are you sure you want to delete this category?')) {
      try {
        await categoryService.delete(id);
        loadCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      category.nameEn?.toLowerCase().includes(search) ||
      category.nameAr?.toLowerCase().includes(search)
    );
  });

  const columns = [
    { header: isRtl ? 'الاسم (إنجليزي)' : 'Name (English)', accessor: 'nameEn' },
    { header: isRtl ? 'الاسم (عربي)' : 'Name (Arabic)', accessor: 'nameAr' },
    {
      header: isRtl ? 'عدد المنتجات' : 'Products',
      accessor: 'productCount',
      render: (value) => value || 0,
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (value) => (
        <Button size="small" variant="danger" onClick={() => handleDelete(value)}>
          {get('delete')}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading categories...'} />;
  }

  return (
    <div className="categories-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة التصنيفات' : 'Category Management'}</h1>
            <p className="page-description">{isRtl ? 'تنظيم مجموعة العطور حسب التصنيفات' : 'Organize your perfume collection by categories'}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {get('add_new_category')}
            </Button>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{categories.length}</div>
              <div className="stat-label">{isRtl ? 'إجمالي التصنيفات' : 'Total Categories'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{categories.reduce((sum, c) => sum + (c.productCount || 0), 0)}</div>
              <div className="stat-label">{isRtl ? 'إجمالي المنتجات' : 'Total Products'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن التصنيفات...' : 'Search categories...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {isRtl ? `${filteredCategories.length} تصنيف` : `${filteredCategories.length} categories`}
          </span>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredCategories}
            emptyMessage={isRtl ? 'لا توجد تصنيفات' : 'No categories found'}
          />
        </div>
      </div>

      {showCreateModal && (
        <CreateCategoryDialog
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadCategories();
          }}
        />
      )}
    </div>
  );
};

export default CategoriesIndex;
